import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Button,
  Typography,
  Spin,
  message,
  Table,
  Space,
  Breadcrumb,
  Tag,
  Popconfirm,
  Modal,
  Form,
  Input,
  Select,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { proposalApi } from '../../services/proposal';
import type { Customer, Product, Proposal } from '../../types/proposal';
import { CustomerForm } from './CustomerForm';
import { ROUTES } from '../../config/routes';
import { PROPOSAL_STATUS_CONFIG } from '../../constants/proposalStatus';

const { Title } = Typography;
const { TextArea } = Input;

// 产品分类选项
const productCategoryOptions = [
  { value: '护肤品', label: '护肤品' },
  { value: '彩妆', label: '彩妆' },
  { value: '个人护理', label: '个人护理' },
  { value: '服装', label: '服装' },
  { value: '配饰', label: '配饰' },
  { value: '数码产品', label: '数码产品' },
  { value: '家用电器', label: '家用电器' },
  { value: '食品', label: '食品' },
  { value: '饮料', label: '饮料' },
  { value: '母婴用品', label: '母婴用品' },
  { value: '家居用品', label: '家居用品' },
  { value: '运动装备', label: '运动装备' },
  { value: '其他', label: '其他' },
];

// 客户路由前缀
const CUSTOMERS_ROUTE = '/customers';

export const CustomerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [proposalsLoading, setProposalsLoading] = useState(false);
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [productFormOpen, setProductFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm] = Form.useForm();

  const fetchCustomer = useCallback(async (customerId: string) => {
    setLoading(true);
    try {
      const response = await proposalApi.getCustomer(customerId);
      setCustomer(response.data);
    } catch (error) {
      message.error('获取客户详情失败');
      navigate(ROUTES.PROPOSALS);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchProducts = useCallback(async (customerId: string) => {
    setProductsLoading(true);
    try {
      const response = await proposalApi.getProducts(customerId);
      setProducts(response.data || []);
    } catch (error) {
      // 错误已处理
    } finally {
      setProductsLoading(false);
    }
  }, []);

  const fetchProposals = useCallback(async (customerId: string) => {
    setProposalsLoading(true);
    try {
      const response = await proposalApi.getProposals({ customer_id: customerId });
      setProposals(response.data || []);
    } catch (error) {
      // 错误已处理
    } finally {
      setProposalsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchCustomer(id);
      fetchProducts(id);
      fetchProposals(id);
    }
  }, [id, fetchCustomer, fetchProducts, fetchProposals]);

  const handleProductCreate = () => {
    setEditingProduct(null);
    productForm.resetFields();
    setProductFormOpen(true);
  };

  const handleProductEdit = (product: Product) => {
    setEditingProduct(product);
    setProductFormOpen(true);
    // 使用 setTimeout 确保 Modal 渲染完成后再设置表单值
    setTimeout(() => {
      productForm.setFieldsValue({
        name: product.name,
        category: product.category,
        description: product.description,
        product_url: product.product_url,
        benchmark_brands: product.benchmark_brands?.map(b => typeof b === 'string' ? b : b.name).join(', '),
      });
    }, 0);
  };

  const handleProductDelete = async (productId: string) => {
    try {
      // 注意：需要后端支持删除产品API
      message.success('产品删除成功');
      if (id) {
        fetchProducts(id);
      }
    } catch (error) {
      message.error('删除产品失败');
    }
  };

  const handleProductSubmit = async () => {
    try {
      const values = await productForm.validateFields();
      const productData = {
        name: values.name,
        category: values.category,
        description: values.description,
        product_url: values.product_url,
        benchmark_brands: values.benchmark_brands?.split(',').map((s: string) => s.trim()).filter(Boolean).map((name: string) => ({ name, link: '' })),
      };

      if (id) {
        if (editingProduct) {
          await proposalApi.updateProduct(id, editingProduct.id, productData);
          message.success('产品更新成功');
        } else {
          await proposalApi.createProduct(id, productData);
          message.success('产品添加成功');
        }
        fetchProducts(id);
      }
      setProductFormOpen(false);
      productForm.resetFields();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleCustomerUpdate = () => {
    setEditFormOpen(false);
    if (id) {
      fetchCustomer(id);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Title level={4}>客户不存在</Title>
        <Button onClick={() => navigate(CUSTOMERS_ROUTE)}>返回列表</Button>
      </div>
    );
  }

  const getStatusTag = (status: string) => {
    const config = PROPOSAL_STATUS_CONFIG[status] || { text: status, color: 'default' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const productColumns = [
    {
      title: '产品名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      render: (text: string) => text ? <Tag color="blue">{text}</Tag> : '-',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => text || '-',
    },
    {
      title: '产品链接',
      dataIndex: 'product_url',
      key: 'product_url',
      ellipsis: true,
      render: (text: string) => text ? <a href={text} target="_blank" rel="noopener noreferrer">{text}</a> : '-',
    },
    {
      title: '竞品品牌',
      dataIndex: 'benchmark_brands',
      key: 'benchmark_brands',
      render: (brands: Array<{ name: string; link: string }> | undefined) => {
        if (!brands?.length) return '-';
        return brands.map((b, index) => <Tag key={b.name || index}>{b.name}</Tag>);
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: unknown, record: Product) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleProductEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确认删除"
            description="确定要删除此产品吗？"
            onConfirm={() => handleProductDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const proposalColumns = [
    {
      title: '提案标题',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: Proposal) => (
        <a onClick={() => navigate(`${ROUTES.PROPOSALS}/${record.id}`)}>{text}</a>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: getStatusTag,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => new Date(text).toLocaleDateString(),
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (text: string) => text ? new Date(text).toLocaleDateString() : '-',
    },
  ];

  return (
    <div>
      {/* 面包屑导航 */}
      <Breadcrumb
        items={[
          { title: <a onClick={() => navigate(ROUTES.CUSTOMERS)}>客户管理</a> },
          { title: customer.company_name },
        ]}
        style={{ marginBottom: 16 }}
      />

      {/* 头部 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(ROUTES.CUSTOMERS)}
            style={{ marginRight: 16 }}
          >
            返回
          </Button>
          <Title level={4} style={{ display: 'inline', margin: 0 }}>
            {customer.company_name}
          </Title>
        </div>
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => setEditFormOpen(true)}
        >
          编辑客户
        </Button>
      </div>

      {/* 客户基本信息 */}
      <Card title="客户基本信息" style={{ marginBottom: 24 }}>
        <Descriptions bordered column={2}>
          <Descriptions.Item label="公司名称">{customer.company_name}</Descriptions.Item>
          <Descriptions.Item label="品牌名称">{customer.brand_name || '-'}</Descriptions.Item>
          <Descriptions.Item label="所属行业">
            {customer.industry ? <Tag color="blue">{customer.industry}</Tag> : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="公司网站">
            {customer.website ? (
              <a href={customer.website} target="_blank" rel="noopener noreferrer">
                {customer.website}
              </a>
            ) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="联系人">{customer.contact_name || '-'}</Descriptions.Item>
          <Descriptions.Item label="联系邮箱">{customer.contact_email || '-'}</Descriptions.Item>
          <Descriptions.Item label="联系电话">{customer.contact_phone || '-'}</Descriptions.Item>
          <Descriptions.Item label="创建时间">
            {new Date(customer.created_at).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="备注说明" span={2}>
            {customer.notes || '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 产品列表 */}
      <Card
        title="产品列表"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleProductCreate}
          >
            添加产品
          </Button>
        }
        style={{ marginBottom: 24 }}
      >
        <Table
          columns={productColumns}
          dataSource={products}
          rowKey="id"
          loading={productsLoading}
          pagination={false}
          locale={{ emptyText: '暂无产品数据' }}
        />
      </Card>

      {/* 关联提案列表 */}
      <Card title="关联提案">
        <Table
          columns={proposalColumns}
          dataSource={proposals}
          rowKey="id"
          loading={proposalsLoading}
          pagination={{
            pageSize: 5,
            showTotal: (total) => `共 ${total} 条`,
          }}
          locale={{ emptyText: '暂无关联提案' }}
        />
      </Card>

      {/* 编辑客户表单 */}
      <CustomerForm
        open={editFormOpen}
        customerId={customer.id}
        onClose={() => setEditFormOpen(false)}
        onSuccess={handleCustomerUpdate}
      />

      {/* 产品表单弹窗 */}
      <Modal
        title={editingProduct ? '编辑产品' : '添加产品'}
        open={productFormOpen}
        onOk={handleProductSubmit}
        onCancel={() => {
          setProductFormOpen(false);
          productForm.resetFields();
        }}
        width={600}
      >
        <Form
          form={productForm}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="产品名称"
            rules={[{ required: true, message: '请输入产品名称' }]}
          >
            <Input placeholder="请输入产品名称" maxLength={100} />
          </Form.Item>

          <Form.Item
            name="category"
            label="产品分类"
          >
            <Select
              placeholder="请选择产品分类"
              allowClear
              options={productCategoryOptions}
            />
          </Form.Item>

          <Form.Item
            name="product_url"
            label="产品链接"
          >
            <Input placeholder="请输入产品链接URL" maxLength={500} />
          </Form.Item>

          <Form.Item
            name="benchmark_brands"
            label="竞品品牌"
            extra="多个品牌用英文逗号分隔"
          >
            <Input placeholder="如：品牌A, 品牌B, 品牌C" maxLength={500} />
          </Form.Item>

          <Form.Item
            name="description"
            label="产品描述"
          >
            <TextArea
              placeholder="请输入产品描述"
              rows={4}
              maxLength={1000}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
